import { useState } from "react";
import { Dropdown, Form } from "react-bootstrap";

interface CreatableSelectProps {
    options: string[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

// A searchable dropdown that also lets the user type and save a custom value
// when none of the preset options match what they need.
const CreatableSelect = ({ options, value, onChange, placeholder = "Select or type...", disabled = false }: CreatableSelectProps) => {
    const [searchText, setSearchText] = useState("");

    const getFilteredOptions = () => {
        const list = Array.isArray(options) ? options : [];
        if (!searchText) return list;
        return list.filter((item) => item?.toLowerCase()?.includes(searchText.toLowerCase()));
    };

    // Show an "add custom value" entry when the typed text isn't an exact preset.
    const trimmedSearch = searchText.trim();
    const hasExactMatch = (Array.isArray(options) ? options : [])
        .some((item) => item?.toLowerCase() === trimmedSearch.toLowerCase());
    const showCreateOption = trimmedSearch.length > 0 && !hasExactMatch;

    const handleSelect = (opt: string) => {
        onChange(opt);
        setSearchText("");
    };

    return (
        <Dropdown className="w-100" onToggle={(isOpen) => { if (!isOpen) setSearchText(""); }}>
            <Dropdown.Toggle
                as="div"
                disabled={disabled}
                className="form-control text-start d-flex justify-content-between align-items-center"
                role="button"
                style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    backgroundColor: disabled ? "#e9ecef" : "#fff",
                    cursor: disabled ? "default" : "pointer",
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {value || placeholder}
                </span>
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ width: "100%", maxHeight: "250px", overflowY: "auto" }}>
                <div className="px-2 pb-2">
                    <Form.Control
                        type="text"
                        placeholder="Search or type a value..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e: any) => {
                            // Enter saves the typed custom value.
                            if (e.key === "Enter" && showCreateOption) {
                                e.preventDefault();
                                handleSelect(trimmedSearch);
                            }
                        }}
                    />
                </div>
                {showCreateOption && (
                    <Dropdown.Item onClick={() => handleSelect(trimmedSearch)}>
                        Use "{trimmedSearch}"
                    </Dropdown.Item>
                )}
                {getFilteredOptions().length > 0 ? (
                    getFilteredOptions().map((item, index) => (
                        <Dropdown.Item
                            key={item + index}
                            onClick={() => handleSelect(item)}
                            style={item === value ? { backgroundColor: "#f0f0f0", color: "#212529" } : {}}
                        >
                            {item === value ? `✓ ${item}` : item}
                        </Dropdown.Item>
                    ))
                ) : (
                    !showCreateOption && <Dropdown.Item disabled>No matches found</Dropdown.Item>
                )}
                {value && (
                    <Dropdown.Item onClick={() => handleSelect("")} className="text-muted">
                        Clear selection
                    </Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default CreatableSelect;
